import { Injectable, OnDestroy } from "@angular/core";
import { EMPTY, Observable, Subject, timer } from "rxjs";
import { delayWhen, retryWhen, share, tap } from "rxjs/operators";
import {
  webSocket,
  WebSocketSubject,
  WebSocketSubjectConfig,
} from "rxjs/webSocket";
import { environment } from "src/environments/environment";
import { LocalStorageService } from "./local-storage.service";

interface ConnectionState {
  socket$?: WebSocketSubject<any>;
  messagesSubject: Subject<any>;
  connectionId?: string;
  currentUrl?: string;
  reconnectAttempts: number;
  explicitlyClosed: boolean;
}

@Injectable({
  providedIn: "root",
})
export class WebsocketService implements OnDestroy {
  private websocket_url_base = environment.websocket_url;

  private userState: ConnectionState = {
    messagesSubject: new Subject<any>(),
    reconnectAttempts: 0,
    explicitlyClosed: false,
  };
  public userMessages$: Observable<any> = this.userState.messagesSubject
    .asObservable()
    .pipe(share());

  // --- State for Bid Round Connection ---
  private bidRoundState: ConnectionState = {
    messagesSubject: new Subject<any>(),
    reconnectAttempts: 0,
    explicitlyClosed: false,
  };
  public bidRoundMessages$: Observable<any> = this.bidRoundState.messagesSubject
    .asObservable()
    .pipe(share());

  private reconnectInterval: number = 5000;
  private maxReconnectInterval: number = 30000;

  constructor(private localStorageService: LocalStorageService) {}

  ngOnDestroy(): void {
    this.closeUserConnection();
    this.closeBidRoundConnection();
  }

  // --- Connection Methods ---

  public connectUser(): void {
    console.log(`Request to connect user WebSocket`);

    if (this.userState.socket$) {
      console.log(`User WebSocket already connected or connecting.`);
      return;
    }

    this.userState.explicitlyClosed = false;
    this.establishConnection("user", this.userState);
  }

  public connectBidRound(bid_round_id: string): void {
    console.log(`Request to connect bid round: ${bid_round_id}`);
    if (
      this.bidRoundState.socket$ &&
      this.bidRoundState.connectionId === bid_round_id
    ) {
      console.log(`Already connected to bid round: ${bid_round_id}`);
      return;
    }
    if (this.bidRoundState.socket$) {
      this.closeBidRoundConnection();
    }
    this.bidRoundState.explicitlyClosed = false;
    this.bidRoundState.connectionId = bid_round_id;
    this.establishConnection("bid", this.bidRoundState, bid_round_id);
  }

  private async establishConnection(
    type: "user" | "bid",
    state: ConnectionState,
    idForUrl?: string
  ): Promise<void> {
    if (type === "bid" && !idForUrl) {
      console.error(`Cannot establish ${type} connection: Missing ID for URL.`);
      return;
    }
    if (state.explicitlyClosed) {
      console.warn(`Cannot establish ${type} connection: Explicitly closed.`);
      return;
    }
    const url = await this.buildUrl(type, idForUrl);

    if (!url) {
      console.error(
        `Cannot establish WebSocket connection (${type}): Failed to build URL (likely missing token).`
      );
      return;
    }

    state.currentUrl = url;
    state.reconnectAttempts = 0;

    console.log(`Attempting to connect WebSocket (${type}) to: ${url}`);

    const config: WebSocketSubjectConfig<any> = {
      url: url,
      openObserver: {
        next: () => {
          console.log(
            `WebSocket connection opened for ${type}${
              type === "bid" && state.connectionId
                ? ": " + state.connectionId
                : ""
            }`
          );
          state.reconnectAttempts = 0;
        },
      },
      closeObserver: {
        next: (closeEvent) => {
          console.log(
            `WebSocket close event for ${type}${
              type === "bid" && state.connectionId
                ? ": " + state.connectionId
                : ""
            }`,
            closeEvent
          );
          if (!state.explicitlyClosed && state.socket$) {
            console.warn(
              `WebSocket for ${type}${
                type === "bid" && state.connectionId
                  ? ": " + state.connectionId
                  : ""
              } closed unexpectedly. State will be cleaned up.`
            );
          }
          if (state.socket$) {
            state.socket$ = undefined;
          }
        },
      },
      closingObserver: {
        next: () => {
          console.log(
            `WebSocket closing for ${type}${
              type === "bid" && state.connectionId
                ? ": " + state.connectionId
                : ""
            }`
          );
        },
      },
    };

    state.socket$ = webSocket(config);

    state.socket$.pipe(this.retryStrategy(type, state)).subscribe(
      (message) => {
        state.messagesSubject.next(message);
      },
      (error) => {
        // Log the raw error to potentially see the invalid message
        console.error("WebSocket raw error object:", error);
        console.error(
          `WebSocket final error for ${type}${
            type === "bid" && state.connectionId
              ? ": " + state.connectionId
              : ""
          }. Likely due to invalid JSON received.`,
          // Only log the error message itself for cleaner console,
          // as the full error object was logged above.
          error?.message || error
        );
        if (state.socket$) {
          state.socket$ = undefined;
        }
      },
      () => {
        console.log(
          `WebSocket stream completed for ${type}${
            type === "bid" && state.connectionId
              ? ": " + state.connectionId
              : ""
          }`
        );
        if (state.socket$) {
          state.socket$ = undefined;
        }
      }
    );
  }

  private async buildUrl(
    type: "user" | "bid",
    id?: string
  ): Promise<string | null> {
    const token = await this.localStorageService.getToken();
    if (!token) {
      console.error(
        "WebSocket connection requires authentication token, but none found."
      );
      return null; // Cannot build URL without token
    }

    const baseUrl = this.websocket_url_base.endsWith("/")
      ? this.websocket_url_base.slice(0, -1)
      : this.websocket_url_base;
    let path = "";
    if (type === "bid") {
      if (!id) {
        console.error(
          "Cannot build WebSocket URL for 'bid' type without an ID."
        );
        return null;
      }
      path = `/bids/${id}`;
    } else if (type === "user") {
      path = `/users/`;
    } else {
      console.error("Invalid connection type for WebSocket");
      return null;
    }

    const url = `${baseUrl}${path}?token=${encodeURIComponent(token)}`;
    return url;
  }

  // --- Retry Strategy ---
  private retryStrategy =
    (type: "user" | "bid", state: ConnectionState) =>
    <T>(attempts: Observable<T>) =>
      attempts.pipe(
        retryWhen((errors) =>
          errors.pipe(
            tap((err) =>
              console.warn(
                `WebSocket error (${type}), attempting reconnect...`,
                err
              )
            ),
            delayWhen(() => {
              if (state.explicitlyClosed) {
                console.log(
                  `Stopping reconnect attempts for ${type} as it was explicitly closed.`
                );
                return EMPTY;
              }

              state.reconnectAttempts++;
              const delay = Math.min(
                this.reconnectInterval *
                  Math.pow(2, state.reconnectAttempts - 1),
                this.maxReconnectInterval
              );
              console.log(
                `Attempt ${
                  state.reconnectAttempts
                } (${type}): Reconnecting WebSocket in ${
                  delay / 1000
                } seconds...`
              );
              return timer(delay);
            })
          )
        )
      );

  // --- Closing Connections ---

  public closeUserConnection(): void {
    this.closeConnection("user", this.userState);
  }

  public closeBidRoundConnection(): void {
    this.closeConnection("bid", this.bidRoundState);
  }

  private closeConnection(type: "user" | "bid", state: ConnectionState): void {
    if (state.socket$) {
      console.log(
        `Closing WebSocket connection explicitly for ${type}${
          type === "bid" && state.connectionId ? ": " + state.connectionId : ""
        }`
      );
      state.explicitlyClosed = true; // Mark as explicitly closed BEFORE completing/unsubscribing
      state.reconnectAttempts = 0;
      state.socket$.complete(); // Gracefully close the connection and stream
      state.socket$ = undefined;
      // Only clear connectionId if it's for a bid round
      if (type === "bid") {
        state.connectionId = undefined;
      }
      state.currentUrl = undefined;
    } else {
      // If socket doesn't exist but we call close, ensure state reflects closed status
      state.explicitlyClosed = true;
      if (type === "bid") {
        state.connectionId = undefined;
      }
      state.currentUrl = undefined;
      state.reconnectAttempts = 0;
    }
  }
}
